// ============================================================
// inboundPreview - what next round does to this body (v8.254.0).
// The INBOUND strip's data: per-turn effects (fire, snares),
// countdown conditions about to tick or lapse, the death clock,
// and regeneration. DISPLAY ONLY - facts are re-derived from the
// condition codes; computeCountdownPure is never called here,
// because it RESOLVES death and regeneration and the real tick
// must stay its alone (the auspex plan's double-fire warning).
// ============================================================
import { getTurnEffects } from './turnEffects';
import { parseExposure, exposureLabel } from './exposures';
import { parseHallucination, bandOf } from './hallucinationCode';
import { condType, condDuration, decodeCond } from './conditions';
import type { Target } from '../types';

export interface InboundItem {
  key: string;
  label: string;
  tone: 'danger' | 'info' | 'good';
}

const COUNTDOWN_TYPES = new Set(['S', 'B', 'D', 'Hw']);

/** The line for something with `left` rounds to run: it ends next round, or it ticks down one. */
function endsOrTicks(code: string, name: string, left: number): InboundItem {
  return left === 1
    ? { key: `end-${code}`, label: `${name} ENDS NEXT ROUND`, tone: 'info' }
    : { key: `tick-${code}`, label: `${name} ${left} -> ${left - 1}`, tone: 'info' };
}

/** Hallucinating (v8.357.0): a STEP falls a rung, a HOLD counts off. */
function hallucinationItem(code: string): InboundItem | null {
  const h = parseHallucination(code);
  if (!h) return null;
  if (h.rounds === null) {
    return h.intensity <= 1
      ? { key: `end-${code}`, label: 'HALLUCINATION ENDS NEXT ROUND', tone: 'info' }
      : { key: `tick-${code}`, label: `HALLUCINATING ${h.intensity} -> ${h.intensity - 1}`, tone: 'info' };
  }
  if (h.rounds === 0) return null;
  return endsOrTicks(code, `HALLUCINATING ${h.intensity} (${bandOf(h.intensity).name.toUpperCase()})`, h.rounds);
}

const countsDown = (ct: string) => COUNTDOWN_TYPES.has(ct) || ct.startsWith('Pen:') || ct.startsWith('Ex:');

/** An exposure's decoded line carries its rounds - the strip says them itself (v8.351.0). */
function countdownName(code: string, ct: string): string {
  const ex = ct.startsWith('Ex:') ? parseExposure(code) : null;
  return ex ? exposureLabel(ex) : decodeCond(code) || code;
}

/** One countdown condition's display line, or null when it has none. */
function countdownItem(code: string): InboundItem | null {
  const ct = condType(code);
  if (ct === 'Hl') return hallucinationItem(code);
  const duration = condDuration(code);
  if (duration <= 0) return null;
  if (ct === 'Dy') return { key: 'clock', label: `DEATH CLOCK ${duration}`, tone: 'danger' };
  return countsDown(ct) ? endsOrTicks(code, countdownName(code, ct), duration) : null;
}

export function deriveInboundPreview(target: Target): InboundItem[] {
  const conditions = target.conditions || [];
  const items: InboundItem[] = [];

  for (const rule of getTurnEffects(conditions, target)) {
    const amount = rule.damage ?? String(rule.flatDamage ?? '');
    items.push({
      key: `effect-${rule.code}`,
      label: amount ? `${rule.label.toUpperCase()} - ${amount} ${rule.damageType}` : rule.label.toUpperCase(),
      tone: 'danger',
    });
  }
  for (const code of conditions) {
    const item = countdownItem(code);
    if (item) items.push(item);
  }
  if ((target.regeneration || 0) > 0)
    items.push({ key: 'regen', label: `REGENERATES ${target.regeneration}`, tone: 'good' });
  return items;
}
